import { useTheme } from '@emotion/react';
import { Code, ContentCopyOutlined, CopyAllOutlined, FileCopy, Link as LinkIcon } from '@mui/icons-material'
import { TabContext } from '@mui/lab';
import { Autocomplete, Box, capitalize, Chip, CircularProgress, Dialog, Divider, Grid, IconButton, InputAdornment, Modal, Paper, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles';
import { ReactComponent as CloseIcon } from "assets/close_icon.svg";
import { FetchRoundAllInvestorsEmails, FetchRoundInvestorsData } from 'api/round';
import { FetchProfileDetails } from 'api/profile';
import EmailAutocomplete from 'common-components/EmailAutocomplete';
import { Field, FormikProvider, useFormik } from 'formik';
import React from 'react'
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import CustomAutoComplete from 'ui-components/CustomAutoComplete';
import CustomTabs from 'ui-components/CustomTabs';
import CustomValidationText from 'ui-components/CustomValidationText';
import CustomInputField from 'ui-components/formik/CustomInputField';
import ModalComponent from 'ui-components/ModalComponent';
import { encode, handleCopyClick, navigateToDomain, decode } from 'utils/index';
import { SET_INVESTOR_DETAILS } from 'store/actions';
import Cookies from 'js-cookie';
import * as Yup from "yup";
import { Primary } from 'ui-components/Buttons';

const useStyles = makeStyles({
    customInput: {
        // '&  > div.MuiInputBase-root': {
        //     padding: 0
        // }
    }
});

let emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const InviteStartupPopup = ({ open, handleClose, handleSubmission, isInvitingStartup = false, slug, affiliate_code, pipeline_id }) => {
    const theme = useTheme();
    const classes = useStyles();
    const dispatch = useDispatch();
    const details = useSelector((state) => state?.investorDetails?.data);
    const investor_id = Cookies.get('X-INV-ID');

    const { refetch: refetchProfileDetails, isLoading: isRefetchingProfile } = FetchProfileDetails({ investor_id });

    const base64URL = encode((affiliate_code ? affiliate_code : "") + (pipeline_id && pipeline_id !== "All" ? "+" + slug : ""))

    const formik = useFormik({
        initialValues: {
            email: "",
            startup_name: '',
            message: '',
            copy_link: '',
        },
        validationSchema: Yup.object({
            startup_name: Yup.string().required("Please enter startup name"),
            email: Yup.string()
                .required("Please enter email")
                .test("email", "Invalid email address", (value) => emailPattern.test(value)),
        }),
        validateOnBlur: false,
        validateOnChange: false,
        validateOnMount: false,
        enableReinitialize: true,
        onSubmit: async (values, { setSubmitting, resetForm }) => {
            const payload = {
                "email": String(values?.email)?.toLowerCase()?.trim(),
                "message": values?.email?.trim(),
                "startup_name": values?.startup_name?.trim(),
                ...(pipeline_id && pipeline_id !== "All" ? { pipelineID: pipeline_id } : {})
            }

            await handleSubmission({ payload })
            resetForm()
        },
    });

    const [copied, setCopied] = React.useState(false)
    const [copiedApply, setCopiedApply] = React.useState(false)
    const [copiedEmbed, setCopiedEmbed] = React.useState(false)
    const [inviteTab, setInviteTab] = React.useState(0)

    const applyFormLink = details?.slug ? navigateToDomain("investor", `${details.slug}/apply`) : null
    const embedCode = applyFormLink ? `<iframe src="${applyFormLink}" width="100%" height="700" frameborder="0" title="Startup Apply Form"></iframe>` : ''
    const inviteLink = navigateToDomain("startup", "register?ref=" + base64URL)

    React.useEffect(() => {
        if (!applyFormLink && (inviteTab === 0 || inviteTab === 1)) {
            setInviteTab(2)
        }
    }, [applyFormLink, inviteTab])

    React.useEffect(() => {
        if (open && !details?.slug) {
            refetchProfileDetails().then((response) => {
                if (response?.data?.data) {
                    dispatch({ type: SET_INVESTOR_DETAILS, data: response.data.data })
                }
            })
        }
    }, [open, details?.slug])

    const LinkBox = ({ value, copied: isCopied, onCopy, description }) => (
        <Grid item xs={12}>
            {/* LINK label — literal Figma grey, not theme.text.secondary */}
            <Typography variant="caption" sx={{ color: '#667085', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1, display: 'block' }}>
                Link
            </Typography>
            {/* Input pill — Figma: 56px tall, radius 10, 1px #E5E7EB, bg #F9FAFB */}
            <Grid container alignItems="center" wrap="nowrap"
                sx={{ bgcolor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '10px', height: 56, pl: '16px', pr: '6px' }}
            >
                <Typography sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.85rem', color: '#667085' }}>
                    {value}
                </Typography>
                {/* Brand gradient CTA (Primary), 40px tall, vertically centred in the pill.
                    Handler and the copied state are unchanged. */}
                <Primary
                    onClick={() => handleCopyClick(value, isCopied, onCopy)}
                    sx={{ height: 40, minWidth: 'auto', px: 2.5, fontSize: '0.875rem', fontWeight: 600, whiteSpace: 'nowrap' }}
                >
                    {isCopied ? '✓ Copied' : 'Copy'}
                </Primary>
            </Grid>
            {description && <Typography variant="caption" sx={{ color: '#667085', mt: 0.75, display: 'block' }}>{description}</Typography>}
        </Grid>
    );

    return (
        <ModalComponent open={open} onClose={handleClose} style={{ maxWidth: 720, width: "100%" }}>
            {/* Figma: radius 16, drop shadow Y20 / blur50 / #000 18%. Height stays
                content-driven (the Email tab is taller than the 350 Application view). */}
            <Paper sx={{ background: theme.palette.customColors.whiteBG, borderRadius: "16px", overflow: 'hidden', boxShadow: '0px 20px 50px rgba(0, 0, 0, 0.18)' }}>
                <IconButton sx={{ position: "absolute", top: 16, right: 16, padding: 0, zIndex: 1 }}>
                    <CloseIcon width={36} height={36} onClick={handleClose} />
                </IconButton>

                {/* Header — no divider here; the only divider is under the tab row (Figma).
                    pt 2rem, and 1.5rem gap below the title before the tabs. */}
                <Grid sx={{ px: "1.5rem", pt: "2rem", pb: "1.5rem" }}>
                    <Typography sx={{ fontSize: "1.25rem", fontWeight: 700, color: "#101828", lineHeight: 1.3 }}>
                        Invite Startup
                    </Typography>
                </Grid>

                {/* Tab bar */}
                <Tabs
                    value={inviteTab}
                    onChange={(_, v) => setInviteTab(v)}
                    variant="fullWidth"
                    sx={{
                        px: '1.5rem',
                        // Figma: unselected grey #667085, selected brand purple #5E4AE3,
                        // divider a literal #E5E7EB (not theme.palette.divider).
                        '& .MuiTab-root': { textTransform: 'none', fontSize: '0.82rem', fontWeight: 600, minHeight: 44, py: 1, color: '#667085' },
                        '& .MuiTab-root.Mui-selected': { color: '#5E4AE3' },
                        borderBottom: '1px solid #E5E7EB',
                    }}
                    TabIndicatorProps={{
                        sx: {
                            height: '2px',
                            border: 'none',
                            background: 'linear-gradient(90deg, #7852F8 0%, #84AEF6 100%)',
                        },
                    }}
                >
                    <Tab label="Application Form" centerRipple={false} disableTouchRipple={true} disabled={!applyFormLink} />
                    <Tab label="Embed" centerRipple={false} disableTouchRipple={true} disabled={!applyFormLink} />
                    <Tab label="Email" centerRipple={false} disableTouchRipple={true} />
                </Tabs>

                <FormikProvider value={formik}>
                    <form onSubmit={formik.handleSubmit} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) e.preventDefault(); }}>
                        {/* Body: 2.25rem top (gap below the tab bar) and 3rem bottom
                            (the roomy padding under the pill that brings the Application
                            view up to ~350 without a fixed height). */}
                        <Grid display="grid" container sx={{ px: "1.5rem", pt: "2.25rem", pb: "3rem", gap: "0rem" }}>

                            {/* Tab 0: Apply Form */}
                            {inviteTab == 0 && (
                                <>
                                    <Grid item xs={12} mb={4}>
                                        <Typography variant="body2" sx={{ color: '#667085', fontSize: '0.9rem' }}>
                                            Paste the application link to invite startups in your deal flow.
                                        </Typography>
                                    </Grid>
                                    {applyFormLink ? (
                                        <LinkBox value={applyFormLink} copied={copiedApply} onCopy={setCopiedApply} />
                                    ) : (
                                        <Grid item xs={12}>
                                            <Typography variant="body2" color="error" sx={{ bgcolor: 'error.light', p: 1.5, borderRadius: '8px' }}>
                                                Your investor profile must be completed before you can access the application form link. Please complete your profile first.
                                            </Typography>
                                        </Grid>
                                    )}
                                </>
                            )}

                            {/* Tab 1: Embed */}
                            {inviteTab == 1 && (
                                <>
                                    <Grid item xs={12} mb={1}>
                                        <Typography variant="body2" sx={{ color: '#667085', fontSize: '0.9rem' }}>
                                            Embed this form directly on your website.
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} mb={2}>
                                        <Typography variant="caption" sx={{ color: '#667085', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.75, display: 'block' }}>
                                            Embed Code
                                        </Typography>
                                        <Box sx={{ bgcolor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', p: 1.5, fontSize: '0.75rem', color: '#555', wordBreak: 'break-all', lineHeight: 1.7 }}>
                                            {embedCode}
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Primary
                                            fullWidth
                                            onClick={() => handleCopyClick(embedCode, copiedEmbed, setCopiedEmbed)}
                                            sx={{ height: 44, fontSize: '0.875rem' }}
                                        >
                                            {copiedEmbed ? 'Copied!' : 'Copy Embed Code'}
                                        </Primary>
                                    </Grid>
                                </>
                            )}

                            {/* Tab 2: Email */}
                            {inviteTab == 2 && (
                                <>
                                    <Grid item xs={12} mb={1}>
                                        <Typography variant="body2" sx={{ color: '#667085', fontSize: '0.9rem' }}>
                                            Send a direct email invitation.
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="caption" sx={{ color: '#667085', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.75, display: 'block' }}>
                                            Startup Name
                                        </Typography>
                                        <CustomInputField
                                            name="startup_name"
                                            // label="Startup Name"
                                            placeholder="Enter startup name"
                                            value={formik?.values?.startup_name}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (value.length <= 30 && /^[^@$()]*$/.test(value)) {
                                                    formik.setFieldValue("startup_name", value.charAt(0).toUpperCase() + value.slice(1));
                                                }
                                            }}
                                            tabIndex={1}
                                            InputProps={{
                                                sx: {
                                                    border: '1px solid #E5E7EB', borderRadius: '8px',
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="caption" sx={{ color: '#667085', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.75, display: 'block' }}>
                                            Founder Email
                                        </Typography>
                                        <CustomInputField
                                            name="email"
                                            // label="Founder Email"
                                            placeholder="founder@startup.com"
                                            value={formik?.values?.email}
                                            onChange={(e) => formik.setFieldValue("email", String(e.target.value)?.toLowerCase())}
                                            tabIndex={2}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Primary type="submit" fullWidth sx={{ height: 52, fontSize: '1rem' }} disabled={isInvitingStartup}>
                                            {isInvitingStartup ? <CircularProgress sx={{ color: "white", marginInline: 'auto' }} size={'1.2rem'} /> : "Send Invitation"}
                                        </Primary>
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </form>
                </FormikProvider>
            </Paper>
        </ModalComponent>
    )
}

export default InviteStartupPopup